import axios from 'axios'

interface PendingRead {
  controller: AbortController
  promise: Promise<unknown>
  subscribers: number
  abortTimer?: ReturnType<typeof setTimeout>
}

const pendingReads = new Map<string, PendingRead>()

export function invalidateSharedReads (): void {
  const sessionPrefix = `${window.localStorage.getItem('sessionToken') ?? ''}:`
  for (const key of pendingReads.keys()) {
    if (key.startsWith(sessionPrefix)) pendingReads.delete(key)
  }
}

export function isReadCancelled (error: unknown): boolean {
  return axios.isCancel(error) || (error instanceof DOMException && error.name === 'AbortError')
}

export async function sharedRead<T> (
  key: string,
  load: (signal: AbortSignal) => Promise<T>,
  signal?: AbortSignal
): Promise<T> {
  if (signal?.aborted) throw new DOMException('Leitura cancelada', 'AbortError')
  const scopedKey = `${window.localStorage.getItem('sessionToken') ?? ''}:${key}`
  let entry = pendingReads.get(scopedKey)
  if (!entry || entry.controller.signal.aborted) {
    const controller = new AbortController()
    const created: PendingRead = { controller, promise: Promise.resolve(), subscribers: 0 }
    created.promise = Promise.resolve().then(async () => await load(controller.signal))
      .finally(() => {
        if (pendingReads.get(scopedKey) === created) pendingReads.delete(scopedKey)
        if (created.abortTimer) clearTimeout(created.abortTimer)
      })
    entry = created
    pendingReads.set(scopedKey, entry)
  }
  const current = entry
  if (current.abortTimer) clearTimeout(current.abortTimer)
  current.subscribers += 1

  return await new Promise<T>((resolve, reject) => {
    let released = false
    const release = (): void => {
      if (released) return
      released = true
      signal?.removeEventListener('abort', abort)
      current.subscribers -= 1
      if (current.subscribers === 0 && pendingReads.get(scopedKey) === current) {
        current.abortTimer = setTimeout(() => {
          if (current.subscribers === 0) current.controller.abort()
        }, 0)
      }
    }
    const abort = (): void => {
      release()
      reject(new DOMException('Leitura cancelada', 'AbortError'))
    }
    signal?.addEventListener('abort', abort, { once: true })
    current.promise.then(value => {
      if (released) return
      release()
      resolve(value as T)
    }, error => {
      if (released) return
      release()
      reject(error)
    })
  })
}
